import { CatalogRepository } from '@lib/api/repository/catalog/catalog.repository';
import { CatalogFilters, CollectionInput } from '@lib/types';
import { GameFilters, GameInput, GameUpdate } from '@lib/types/game';

export default class CatalogService {
  constructor(private repository: CatalogRepository) {}

  adminCollectionDetails(id: string, signal?: AbortSignal) {
    return this.repository.adminCollectionDetails(id, signal);
  }

  landingCatalog(signal?: AbortSignal) {
    return this.repository.landingCatalog(signal);
  }

  eventGames(historical: boolean, signal?: AbortSignal) {
    return this.repository.eventGames(historical, signal);
  }

  adminGames(filters: GameFilters, signal?: AbortSignal) {
    return this.repository.adminGames(filters, signal);
  }

  createGame(input: GameInput) {
    return this.repository.createGame(input);
  }

  updateGame(id: string, input: GameUpdate) {
    return this.repository.updateGame(id, input);
  }

  async adminGameChoices(signal?: AbortSignal) {
    const first = await this.repository.adminGames({ page: 1 }, signal);
    const remaining = await Promise.all(
      Array.from(
        { length: Math.max(0, Math.ceil(first.total / first.limit) - 1) },
        (_, index) => this.repository.adminGames({ page: index + 2 }, signal),
      ),
    );

    return [...first.items, ...remaining.flatMap((page) => page.items)];
  }

  games(signal?: AbortSignal) {
    return this.repository.games(signal);
  }

  collections(gameCode: string, signal?: AbortSignal) {
    return this.repository.collections(gameCode, signal);
  }

  adminCollections(gameCode: string, signal?: AbortSignal) {
    return this.repository.adminCollections(gameCode, signal);
  }

  rarities(signal?: AbortSignal) {
    return this.repository.rarities(signal);
  }

  languages(signal?: AbortSignal) {
    return this.repository.languages(signal);
  }

  preview(search: string, gameCode?: string, signal?: AbortSignal) {
    return this.repository.preview(search, gameCode, signal);
  }

  latestSynchronization(signal?: AbortSignal) {
    return this.repository.latestSynchronization(signal);
  }

  allCollectionCards(collectionId: string, signal?: AbortSignal) {
    return this.repository.allCollectionCards(collectionId, signal);
  }

  collectionCards(
    collectionId: string,
    cursor?: string,
    signal?: AbortSignal,
    page?: number,
  ) {
    return this.repository.collectionCards(collectionId, cursor, signal, page);
  }

  details(gameCode: string, cardSlug: string, signal?: AbortSignal) {
    return this.repository.details(gameCode, cardSlug, signal);
  }

  options(cardId: string, signal?: AbortSignal) {
    return this.repository.options(cardId, signal);
  }

  list(
    filters?: CatalogFilters & { cursor?: string; page?: number },
    signal?: AbortSignal,
  ) {
    return this.repository.list(filters, signal);
  }

  adminCards(
    filters?: CatalogFilters & { cursor?: string; page?: number },
    signal?: AbortSignal,
  ) {
    return this.repository.adminCards(filters, signal);
  }

  search(filters: CatalogFilters, signal?: AbortSignal) {
    return this.repository.search(filters, signal);
  }

  synchronize() {
    return this.repository.synchronize();
  }

  listRarities(signal?: AbortSignal) {
    return this.repository.listRarities(signal);
  }

  createRarity(data: {
    name: string;
    shortName?: string | null;
    code: string;
  }) {
    return this.repository.createRarity(data);
  }

  updateRarity(
    id: string,
    data: { name: string; shortName?: string | null; code: string },
  ) {
    return this.repository.updateRarity(id, data);
  }

  deleteRarity(id: string) {
    return this.repository.deleteRarity(id);
  }

  listCollections(signal?: AbortSignal) {
    return this.repository.listCollections(signal);
  }

  createCollection(data: CollectionInput) {
    return this.repository.createCollection(data);
  }

  updateCollection(id: string, data: Partial<CollectionInput>) {
    return this.repository.updateCollection(id, data);
  }

  deleteCollection(id: string) {
    return this.repository.deleteCollection(id);
  }

  createCard(data: import('@lib/types').CreateCardInput) {
    return this.repository.createCard(data);
  }

  adminCard(cardId: string, signal?: AbortSignal) {
    return this.repository.adminCard(cardId, signal);
  }

  updateCard(cardId: string, data: import('@lib/types').UpdateCardInput) {
    return this.repository.updateCard(cardId, data);
  }

  deleteCard(cardId: string) {
    return this.repository.deleteCard(cardId);
  }
}
